import React, { useState, useEffect } from 'react';
import { X, Clock, Save, CalendarDays } from 'lucide-react';
import api from '../utils/api';

const WEEK_DAYS = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sab' }
];

export default function WorkingHoursModal({ isOpen, onClose, barbershopId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState({
    hora_abertura: '09:00',
    hora_fechamento: '18:00',
    hora_almoco_inicio: '12:00',
    hora_almoco_fim: '13:00',
    intervalo_minutos: 30,
    dias_funcionamento: [1, 2, 3, 4, 5, 6]
  });

  const loadHours = async () => {
    try {
      const response = await api.get(`/barbershops/${barbershopId}`);
      const data = response.data;
      
      if (data) {
        setHours({
          hora_abertura: data.hora_abertura || '09:00',
          hora_fechamento: data.hora_fechamento || '18:00',
          hora_almoco_inicio: data.hora_almoco_inicio || '12:00',
          hora_almoco_fim: data.hora_almoco_fim || '13:00',
          intervalo_minutos: data.intervalo_minutos || 30,
          dias_funcionamento: Array.isArray(data.dias_funcionamento) && data.dias_funcionamento.length
            ? data.dias_funcionamento
            : [1, 2, 3, 4, 5, 6]
        });
      }
    } catch (error) {
      console.error('Erro ao carregar horários:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hours.dias_funcionamento.length) {
      alert('Selecione pelo menos um dia de funcionamento.');
      return;
    }

    setLoading(true);
    
    try {
      await api.put(`/barbershops/${barbershopId}`, hours);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar horários:', error);
      alert('Erro ao salvar horários. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkingDay = (day) => {
    setHours((current) => {
      const isSelected = current.dias_funcionamento.includes(day);
      const dias_funcionamento = isSelected
        ? current.dias_funcionamento.filter((item) => item !== day)
        : [...current.dias_funcionamento, day].sort((a, b) => a - b);

      return { ...current, dias_funcionamento };
    });
  };

  useEffect(() => {
    if (isOpen) {
      loadHours();
    }
  }, [isOpen, barbershopId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Clock className="w-6 h-6 text-purple-500" />
            <h2 className="text-xl font-bold text-white">Horários de Funcionamento</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="w-5 h-5 text-purple-400" />
              <h3 className="text-lg font-semibold text-white">Dias de Funcionamento</h3>
            </div>

            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
              {WEEK_DAYS.map((day) => {
                const selected = hours.dias_funcionamento.includes(day.value);

                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleWorkingDay(day.value)}
                    className={`h-11 rounded-lg border text-sm font-semibold transition-colors ${
                      selected
                        ? 'border-purple-500 bg-purple-600 text-white'
                        : 'border-gray-700 bg-gray-900 text-gray-400 hover:border-purple-500'
                    }`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Horário de Abertura e Fechamento */}
          <div className="card p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Horário de Funcionamento</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Horário de Abertura
                </label>
                <input
                  type="time"
                  value={hours.hora_abertura}
                  onChange={(e) => setHours({...hours, hora_abertura: e.target.value})}
                  className="input"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Horário de Fechamento
                </label>
                <input
                  type="time"
                  value={hours.hora_fechamento}
                  onChange={(e) => setHours({...hours, hora_fechamento: e.target.value})}
                  className="input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Intervalo de Almoço */}
          <div className="card p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Horário de Almoço</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Início do Almoço
                </label>
                <input
                  type="time"
                  value={hours.hora_almoco_inicio}
                  onChange={(e) => setHours({...hours, hora_almoco_inicio: e.target.value})}
                  className="input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Fim do Almoço
                </label>
                <input
                  type="time"
                  value={hours.hora_almoco_fim}
                  onChange={(e) => setHours({...hours, hora_almoco_fim: e.target.value})}
                  className="input"
                />
              </div>
            </div>
          </div>

          {/* Intervalo entre agendamentos */}
          <div className="card p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Intervalo entre Agendamentos</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Duração do intervalo (minutos)
              </label>
              <select
                value={hours.intervalo_minutos}
                onChange={(e) => setHours({...hours, intervalo_minutos: parseInt(e.target.value)})}
                className="input"
              >
                <option value={15}>15 minutos</option>
                <option value={30}>30 minutos</option>
                <option value={45}>45 minutos</option>
                <option value={60}>60 minutos</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Define o intervalo entre os horários disponíveis para agendamento
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost px-6 py-2 bg-gray-700 text-gray-300 hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary px-6 py-2 bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Horários
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
